import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * LIVE VAD / turn-detection test bench (Bug 5).
 *
 * Streams mic PCM (16 kHz) over ws/vad-test/ to the server, which runs the SAME
 * per-chunk turn-detection state machine as the live cost-optimized interview
 * (RMS pre-gate → Silero speech probability → onset debounce → end-of-speech
 * silence clock). The panel flags a turn the instant it opens (speech_start) and
 * the instant it ends (silence crossing SILENCE_TIMEOUT_MS) — so an admin can
 * speak / go quiet / make noise and watch turns detected live, exactly as the
 * interview would, and tune the thresholds against real room noise. Ephemeral.
 */
import { useConfigContext } from '@/hoc/context/config-provider';
import { useToast } from '@/hoc';
import { float32ToPcm16ArrayBuffer, resampleMonoFloat32 } from '@/context/realtime/audio-utils';
import { CheckCircle2, Loader2, Mic, RefreshCw, Save, Square, XCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Button } from '../ui';
import { useSaveVadSettings } from './voice-admin-api';
const TARGET_RATE = 16000;
function getAudioContext() {
    const Ctor = window.AudioContext ||
        window.webkitAudioContext;
    return new Ctor();
}
/** Build the VAD-test WS URL from the interview WS base in config. */
function vadWsUrl(webSocketUrl) {
    return (webSocketUrl || '').replace(/ws\/interview\/?$/, 'ws/vad-test/');
}
const VadTestPanel = () => {
    const { config } = useConfigContext();
    const { showToast } = useToast();
    const saveVad = useSaveVadSettings();
    const [wsState, setWsState] = useState('connecting');
    const [available, setAvailable] = useState(null);
    const [recording, setRecording] = useState(false);
    // Live telemetry.
    const [turnState, setTurnState] = useState('idle');
    const [prob, setProb] = useState(0);
    const [rms, setRms] = useState(0);
    const [silenceMs, setSilenceMs] = useState(0);
    const [turns, setTurns] = useState([]);
    const turnCountRef = useRef(0);
    // Tunable controls (defaults are overwritten by the server 'ready' params).
    const [threshold, setThreshold] = useState(0.5);
    const [onsetMinFrames, setOnsetMinFrames] = useState(3);
    const [silenceTimeoutMs, setSilenceTimeoutMs] = useState(1100);
    const [pregateRms, setPregateRms] = useState(60);
    const wsRef = useRef(null);
    const streamRef = useRef(null);
    const ctxRef = useRef(null);
    const procRef = useRef(null);
    const sourceRef = useRef(null);
    const stopMic = () => {
        var _a, _b, _c, _d, _e;
        try {
            (_a = procRef.current) === null || _a === void 0 ? void 0 : _a.disconnect();
            (_b = sourceRef.current) === null || _b === void 0 ? void 0 : _b.disconnect();
            (_c = streamRef.current) === null || _c === void 0 ? void 0 : _c.getTracks().forEach((t) => t.stop());
            (_e = (_d = ctxRef.current) === null || _d === void 0 ? void 0 : _d.close) === null || _e === void 0 ? void 0 : _e.call(_d);
        }
        catch (_f) {
            /* best effort */
        }
        procRef.current = null;
        sourceRef.current = null;
        streamRef.current = null;
        ctxRef.current = null;
    };
    const openSocket = () => {
        setWsState('connecting');
        let ws;
        try {
            ws = new WebSocket(vadWsUrl(config.webSocketUrl));
        }
        catch (_a) {
            setWsState('error');
            return;
        }
        ws.binaryType = 'arraybuffer';
        wsRef.current = ws;
        ws.onmessage = (evt) => {
            var _a, _b, _c, _d;
            let msg;
            try {
                msg = JSON.parse(evt.data);
            }
            catch (_e) {
                return;
            }
            switch (msg.type) {
                case 'ready':
                    setWsState('ready');
                    setAvailable((_a = msg.available) !== null && _a !== void 0 ? _a : null);
                    if (msg.params) {
                        if (msg.params.threshold != null)
                            setThreshold(msg.params.threshold);
                        if (msg.params.onset_min_frames != null)
                            setOnsetMinFrames(msg.params.onset_min_frames);
                        if (msg.params.silence_timeout_ms != null)
                            setSilenceTimeoutMs(msg.params.silence_timeout_ms);
                        if (msg.params.pregate_rms != null)
                            setPregateRms(msg.params.pregate_rms);
                    }
                    break;
                case 'vad':
                    setTurnState(msg.state || 'idle');
                    setProb((_b = msg.prob) !== null && _b !== void 0 ? _b : 0);
                    setRms((_c = msg.rms) !== null && _c !== void 0 ? _c : 0);
                    setSilenceMs((_d = msg.silence_ms) !== null && _d !== void 0 ? _d : 0);
                    break;
                case 'speech_start':
                    turnCountRef.current += 1;
                    setTurnState('speaking');
                    break;
                case 'turn_end':
                    setTurns((prev) => {
                        var _a, _b, _c, _d, _e;
                        return [
                            {
                                n: turnCountRef.current,
                                startMs: ((_a = msg.t_ms) !== null && _a !== void 0 ? _a : 0) - ((_b = msg.speech_ms) !== null && _b !== void 0 ? _b : 0) - ((_c = msg.silence_ms) !== null && _c !== void 0 ? _c : 0),
                                speechMs: (_d = msg.speech_ms) !== null && _d !== void 0 ? _d : 0,
                                silenceMs: (_e = msg.silence_ms) !== null && _e !== void 0 ? _e : 0,
                            },
                            ...prev,
                        ];
                    });
                    setTurnState('idle');
                    setSilenceMs(0);
                    break;
                default:
                    break;
            }
        };
        ws.onerror = () => setWsState((s) => (s === 'ready' ? s : 'error'));
        ws.onclose = () => {
            setWsState('closed');
            setRecording(false);
        };
    };
    useEffect(() => {
        openSocket();
        return () => {
            stopMic();
            if (wsRef.current && wsRef.current.readyState <= 1) {
                try {
                    wsRef.current.close();
                }
                catch (_a) {
                    /* ignore */
                }
            }
            wsRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const sendConfig = (overrides) => {
        const ws = wsRef.current;
        if (!ws || ws.readyState !== WebSocket.OPEN)
            return;
        ws.send(JSON.stringify(Object.assign({ type: 'config', threshold, onset_min_frames: onsetMinFrames, silence_timeout_ms: silenceTimeoutMs, pregate_rms: pregateRms }, overrides)));
    };
    const startRecording = () => {
        const ws = wsRef.current;
        if (!ws || ws.readyState !== WebSocket.OPEN)
            return;
        ws.send(JSON.stringify({ type: 'reset' }));
        setTurns([]);
        turnCountRef.current = 0;
        navigator.mediaDevices
            .getUserMedia({ audio: { echoCancellation: true, noiseSuppression: false } })
            .then((stream) => {
            const ctx = getAudioContext();
            const source = ctx.createMediaStreamSource(stream);
            const proc = ctx.createScriptProcessor(4096, 1, 1);
            proc.onaudioprocess = (e) => {
                const sock = wsRef.current;
                if (!sock || sock.readyState !== WebSocket.OPEN)
                    return;
                const input = new Float32Array(e.inputBuffer.getChannelData(0));
                const resampled = resampleMonoFloat32(input, ctx.sampleRate, TARGET_RATE);
                sock.send(float32ToPcm16ArrayBuffer(resampled));
            };
            source.connect(proc);
            proc.connect(ctx.destination);
            streamRef.current = stream;
            ctxRef.current = ctx;
            procRef.current = proc;
            sourceRef.current = source;
            setRecording(true);
        })
            .catch(() => {
            setWsState((s) => s);
            stopMic();
        });
    };
    const stopRecording = () => {
        stopMic();
        setRecording(false);
        setTurnState('idle');
        const ws = wsRef.current;
        if (ws && ws.readyState === WebSocket.OPEN)
            ws.send(JSON.stringify({ type: 'reset' }));
    };
    const saveAsDefaults = () => {
        saveVad.mutate({
            threshold,
            onset_min_frames: onsetMinFrames,
            silence_timeout_ms: silenceTimeoutMs,
            pregate_rms: pregateRms,
        }, {
            onSuccess: () => showToast({ message: 'Saved as live interview defaults', status: 'success' }),
            onError: (e) => showToast({ message: (e === null || e === void 0 ? void 0 : e.error) || 'Save failed', status: 'error' }),
        });
    };
    const badge = turnState === 'speaking'
        ? { text: '🗣 Speaking', cls: 'bg-dash-success/15 text-dash-successInk ring-1 ring-dash-success/40' }
        : turnState === 'arming'
            ? { text: '… detecting', cls: 'bg-amber-100 text-amber-700 ring-1 ring-amber-300' }
            : { text: 'Idle / listening', cls: 'bg-dash-surface text-dash-muted' };
    const silencePct = Math.min(100, (silenceMs / Math.max(1, silenceTimeoutMs)) * 100);
    return (_jsxs("div", { className: 'space-y-5 p-4', children: [_jsxs("p", { className: 'text-sm text-dash-muted', children: ["Streams your mic through the ", _jsx("strong", { children: "same turn detector the live interview uses" }), ". Speak, then go quiet \u2014 a turn is flagged the moment speech starts, and ends the moment your silence crosses the timeout below. Try coughing / typing to confirm noise no longer opens a turn."] }), wsState === 'connecting' && (_jsxs("p", { className: 'flex items-center gap-2 text-sm text-dash-muted', children: [_jsx(Loader2, { className: 'h-4 w-4 animate-spin' }), " Connecting to the VAD bench\u2026"] })), (wsState === 'error' || wsState === 'closed') && (_jsxs("div", { className: 'flex items-center justify-between rounded-md bg-dash-danger/10 px-3 py-2 text-sm text-dash-dangerInk', children: [_jsxs("span", { className: 'flex items-center gap-2', children: [_jsx(XCircle, { className: 'h-4 w-4' }), " Bench disconnected."] }), _jsx(Button, { variant: 'outline', size: 'sm', onClick: openSocket, rightIcon: _jsx(RefreshCw, { className: 'h-4 w-4' }), children: "Reconnect" })] })), wsState === 'ready' && available === false && (_jsxs("p", { className: 'flex items-center gap-2 text-sm text-dash-dangerInk', children: [_jsx(XCircle, { className: 'h-4 w-4' }), " Silero VAD model unavailable on the server \u2014 turn detection would fall back to RMS."] })), _jsxs("div", { className: 'grid grid-cols-2 gap-4 rounded-xl border border-dash-border p-4 md:grid-cols-4', children: [_jsxs("label", { className: 'block text-xs font-medium text-dash-muted', children: ["Speech threshold: ", _jsx("span", { className: 'text-dash-text', children: threshold.toFixed(2) }), _jsx("input", { type: 'range', min: 0, max: 1, step: 0.01, value: threshold, onChange: (e) => { const v = Number(e.target.value); setThreshold(v); sendConfig({ threshold: v }); }, className: 'mt-1 w-full' })] }), _jsxs("label", { className: 'block text-xs font-medium text-dash-muted', children: ["Onset min frames: ", _jsx("span", { className: 'text-dash-text', children: onsetMinFrames }), _jsx("input", { type: 'range', min: 1, max: 12, step: 1, value: onsetMinFrames, onChange: (e) => { const v = Number(e.target.value); setOnsetMinFrames(v); sendConfig({ onset_min_frames: v }); }, className: 'mt-1 w-full' })] }), _jsxs("label", { className: 'block text-xs font-medium text-dash-muted', children: ["Silence timeout: ", _jsxs("span", { className: 'text-dash-text', children: [silenceTimeoutMs, " ms"] }), _jsx("input", { type: 'range', min: 300, max: 3000, step: 50, value: silenceTimeoutMs, onChange: (e) => { const v = Number(e.target.value); setSilenceTimeoutMs(v); sendConfig({ silence_timeout_ms: v }); }, className: 'mt-1 w-full' })] }), _jsxs("label", { className: 'block text-xs font-medium text-dash-muted', children: ["RMS pre-gate: ", _jsx("span", { className: 'text-dash-text', children: pregateRms }), _jsx("input", { type: 'range', min: 0, max: 500, step: 10, value: pregateRms, onChange: (e) => { const v = Number(e.target.value); setPregateRms(v); sendConfig({ pregate_rms: v }); }, className: 'mt-1 w-full' })] }), _jsxs("div", { className: 'col-span-2 flex items-center justify-between gap-2 border-t border-dash-border pt-3 md:col-span-4', children: [_jsx("p", { className: 'text-xs text-dash-muted', children: "These sliders start at the current live interview defaults. Save to apply them to real cost-optimized interviews." }), _jsx(Button, { variant: 'outline', size: 'sm', onClick: saveAsDefaults, isLoading: saveVad.isPending, rightIcon: _jsx(Save, { className: 'h-4 w-4' }), children: "Save as interview defaults" })] })] }), _jsxs("div", { className: 'flex flex-wrap items-center gap-4', children: [recording ? (_jsx(Button, { variant: 'dangerSecondary', onClick: stopRecording, rightIcon: _jsx(Square, { className: 'h-4 w-4' }), children: "Stop" })) : (_jsx(Button, { variant: 'primary', onClick: startRecording, disabled: wsState !== 'ready', rightIcon: _jsx(Mic, { className: 'h-4 w-4' }), children: "Start speaking test" })), _jsx("span", { className: `rounded-full px-3 py-1 text-sm font-medium ${badge.cls}`, children: badge.text }), _jsxs("span", { className: 'text-sm text-dash-muted', children: ["turns detected: ", _jsx("strong", { className: 'text-dash-text', children: turns.length })] })] }), recording && (_jsxs("div", { className: 'space-y-3 rounded-xl border border-dash-border p-4', children: [_jsxs("div", { children: [_jsxs("div", { className: 'mb-1 flex justify-between text-xs text-dash-muted', children: [_jsx("span", { children: "speech probability" }), _jsxs("span", { className: 'text-dash-text', children: [prob.toFixed(2), " \u00B7 RMS ", rms] })] }), _jsx("div", { className: 'h-3 w-full overflow-hidden rounded-full bg-dash-surface', children: _jsx("div", { className: `h-full transition-all ${prob >= threshold ? 'bg-dash-success' : 'bg-dash-muted/50'}`, style: { width: `${Math.max(2, prob * 100)}%` } }) })] }), _jsxs("div", { children: [_jsxs("div", { className: 'mb-1 flex justify-between text-xs text-dash-muted', children: [_jsx("span", { children: "silence toward turn-end" }), _jsxs("span", { className: 'text-dash-text', children: [Math.round(silenceMs), " / ", silenceTimeoutMs, " ms"] })] }), _jsx("div", { className: 'h-3 w-full overflow-hidden rounded-full bg-dash-surface', children: _jsx("div", { className: 'h-full bg-dash-danger transition-all', style: { width: `${silencePct}%` } }) })] })] })), turns.length > 0 && (_jsxs("div", { className: 'rounded-xl border border-dash-border p-4', children: [_jsx("p", { className: 'mb-2 text-sm font-semibold text-dash-text', children: "Detected turns (newest first)" }), _jsx("div", { className: 'max-h-56 space-y-1 overflow-y-auto text-sm', children: turns.map((t, i) => (_jsxs("div", { className: 'flex items-center gap-3 rounded-md bg-dash-surface px-3 py-1.5', children: [_jsx(CheckCircle2, { className: 'h-4 w-4 shrink-0 text-dash-successInk' }), _jsxs("span", { className: 'text-dash-text', children: ["Turn ", t.n] }), _jsxs("span", { className: 'text-dash-muted', children: ["spoke ~", (t.speechMs / 1000).toFixed(1), "s"] }), _jsxs("span", { className: 'text-dash-muted', children: ["\u2192 ended after ", (t.silenceMs / 1000).toFixed(1), "s silence"] })] }, i))) })] }))] }));
};
export default VadTestPanel;
